import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { DistrictAdminService } from '../../../../../services/district-admin.service';
import { Integration } from '../../../../../models/integration';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmCancelDialog } from '../../../../common/confirm-cancel-dialog/confirm-cancel-dialog';

@Component({
  selector: 'app-sis-dialog',
  imports: [
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatIcon,
    MatButtonModule,
    MatSlideToggleModule,
  ],
  templateUrl: './sis-dialog.html',
  styleUrl: './sis-dialog.css',
})
export class SisDialog {
  districtService = inject(DistrictAdminService);
  dialogRef = inject(MatDialogRef);
  data = inject<{ integration: Integration | null } | null>(MAT_DIALOG_DATA) ?? {
    integration: null,
  };

  private dialog = inject(MatDialog);

  private readonly MAX_ZIP_SIZE_BYTES = 50 * 1024 * 1024;

  form = new FormGroup({
    connectionType: new FormControl<'API' | 'CSV'>(this.data.integration?.type ?? 'API', {
      nonNullable: true,
      validators: Validators.required,
    }),
    baseUrl: new FormControl(this.data.integration?.baseUrl ?? ''),
    clientId: new FormControl(this.data.integration?.credentials?.clientId ?? ''),
    clientSecret: new FormControl(''), // see note below on why this stays blank
    syncFrequency: new FormControl(this.data.integration?.syncFrequency ?? 'DAILY', {
      nonNullable: true,
      validators: Validators.required,
    }),
    automaticSync: new FormControl(this.data.integration?.automaticSync ?? true, {
      nonNullable: true,
    }),
    zipFile: new FormControl<File | null>(null),
  });
  ngOnInit() {
    this.applyConditionalValidators(this.form.controls.connectionType.value);
    this.form.controls.connectionType.valueChanges.subscribe((type) => {
      this.applyConditionalValidators(type);
    });
  }
  applyConditionalValidators(type: 'API' | 'CSV') {
    const apiControls = [
      this.form.controls.baseUrl,
      this.form.controls.clientId,
      this.form.controls.clientSecret,
    ];
    const csvControl = this.form.controls.zipFile;

    if (type === 'API') {
      apiControls.forEach((c) => c.setValidators(Validators.required));
      csvControl.clearValidators();
    } else {
      apiControls.forEach((c) => c.clearValidators());
      csvControl.setValidators(Validators.required);
    }

    [...apiControls, csvControl].forEach((c) => c.updateValueAndValidity({ emitEvent: false }));
  }

  integrate() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const dialogRef = this.dialog.open(ConfirmCancelDialog, {
      data: {
        title: 'Add this integration',
        content:
          'This will connect your district to the selected data source. Are you sure you want to continue?',
        confirmLabel: 'Integrate',
        icon: 'sync',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.performIntegration();
      }
    });
  }

  private performIntegration() {
    const raw = this.form.getRawValue();
    const formData = new FormData();
    formData.append('connectionType', raw.connectionType);
    formData.append('syncFrequency', raw.syncFrequency);
    formData.append('automaticSync', String(raw.automaticSync));

    if (raw.connectionType === 'API') {
      formData.append('baseUrl', raw.baseUrl ?? '');
      formData.append('clientId', raw.clientId ?? '');
      formData.append('clientSecret', raw.clientSecret ?? '');
    } else if (raw.zipFile) {
      formData.append('zipFile', raw.zipFile, raw.zipFile.name);
    }

    this.districtService.addIntegration(formData).subscribe({
      next: (integration) => {
        this.dialogRef.close({ data: integration });
      },
      error: (err) => {
        console.error('addIntegration failed:', err);
      },
    });
  }

  onZipAdded(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;

    if (!file) {
      this.form.controls.zipFile.setValue(null);
      return;
    }

    const isZip =
      file.type === 'application/zip' ||
      file.type === 'application/x-zip-compressed' ||
      file.name.toLowerCase().endsWith('.zip');

    if (!isZip) {
      this.form.controls.zipFile.setErrors({ invalidType: true });
      this.form.controls.zipFile.setValue(null);
      input.value = '';
      return;
    }

    if (file.size > this.MAX_ZIP_SIZE_BYTES) {
      this.form.controls.zipFile.setErrors({ tooLarge: true });
      this.form.controls.zipFile.setValue(null);
      input.value = '';
      return;
    }

    this.form.controls.zipFile.setValue(file);
    this.form.controls.zipFile.markAsTouched();
  }
}
